import { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { black, white, transparent, lighterGray, purpleBlack } from 'shared/styles/colors';
import * as ColorSets from 'shared/types/colorSets';
import { Category, Breakpoint } from 'shared/types';
import * as LayoutUtils from '../utils/LayoutUtils';
import { media } from "@mverissimoo/emotion-grid";
import { Alt1ColorAccessoriesList, Alt3ColorAccessoriesList, Alt4ColorAccessoriesList, Alt7ColorAccessoriesList } from 'shared/types/optionSets';
import { ColorState, OptionState } from '../types';

interface Props {
  category: string;
  optionState: OptionState;
  colorState: ColorState;
  updateColor: (selectedColor: string) => void;
  isMainView: boolean;
}

interface ColorContainerProps {
  isSelected: boolean;
  color: string;
}

const ColorPicker = ({ category, optionState, colorState, updateColor, isMainView }: Props) => {
  const [colors, setColors] = useState<string[]>([]);
  const [currentColor, setCurrentColor] = useState<string>(transparent);

  const updateAccessoryColor = (selectedOption: number, lastSelectedColor: string) => {
    // For special color sets, change color to first in list if
    // previously selected color is not an option
    if (Alt1ColorAccessoriesList.includes(selectedOption)) {
      setColors(ColorSets.AccessoryAlt1BaseColors);
      if (!ColorSets.AccessoryAlt1BaseColors.includes(lastSelectedColor)) {
        changeColor(ColorSets.AccessoryAlt1BaseColors[0]);
      }
    } else if (Alt3ColorAccessoriesList.includes(selectedOption)) {
      setColors(ColorSets.AccessoryAlt3BaseColors);
      if (!ColorSets.AccessoryAlt3BaseColors.includes(lastSelectedColor)) {
        changeColor(ColorSets.AccessoryAlt3BaseColors[0]);
      }
    } else if (Alt4ColorAccessoriesList.includes(selectedOption)) {
      setColors(ColorSets.AccessoryAlt4BaseColors);
      if (!ColorSets.AccessoryAlt4BaseColors.includes(lastSelectedColor)) {
        changeColor(ColorSets.AccessoryAlt4BaseColors[0]);
      }
    } else if (Alt7ColorAccessoriesList.includes(selectedOption)) {
      setColors(ColorSets.AccessoryAlt7BaseColors);
      if (!ColorSets.AccessoryAlt7BaseColors.includes(lastSelectedColor)) {
        changeColor(ColorSets.AccessoryAlt7BaseColors[0]);
      }
    } else {
      setColors(ColorSets.AccessoryBaseColors);
      if (!ColorSets.AccessoryBaseColors.includes(lastSelectedColor)) {
        changeColor(ColorSets.AccessoryBaseColors[0]);
      }
    }
    setCurrentColor(lastSelectedColor);
  };

  useEffect(() => {
    const refreshColors = () => {
      switch (category) {
        case Category.HAIR:
          setColors(ColorSets.HairBaseColors);
          setCurrentColor(colorState.hair);
          break;
        case Category.HEAD:
          setColors(ColorSets.SkinColors);
          setCurrentColor(colorState.skin);
          break;
        case Category.MOUTH:
          setColors(ColorSets.MouthColors);
          setCurrentColor(colorState.mouth);
          break;
        case Category.EARS:
          setColors(ColorSets.SkinColors);
          setCurrentColor(colorState.skin);
          break;
        case Category.FACIAL_HAIR:
          setColors(ColorSets.HairBaseColors);
          setCurrentColor(colorState.hair);
          break;
        case Category.ACCESSORIES_EYEWEAR: {
          updateAccessoryColor(optionState.accessories.eyewear, colorState.accessories.eyewear);
          break;
        }
        case Category.ACCESSORIES_HEADGEAR: {
          updateAccessoryColor(optionState.accessories.headgear, colorState.accessories.headgear);
          break;
        }
        case Category.ACCESSORIES_FANFETTI: {
          updateAccessoryColor(optionState.accessories.fanfetti, colorState.accessories.fanfetti);
          break;
        }
        case (Category.BACKGROUND):
          setColors(ColorSets.BackgroundBaseColors);
          setCurrentColor(colorState.background);
          break;
        default:
          setColors([]);
      }
    }

    refreshColors();
  });

  const changeColor = (color: string) => {
    setCurrentColor(color);
    updateColor(color);
  };

  return (
    <OuterContainer>
      {isMainView &&
        <ColorGrid>
          {colors.map((item) => {
            return (
              <ColorSquare
                isSelected={item === currentColor}
                color={item}
                onClick={() => changeColor(item)}
              />
            );
          })}
        </ColorGrid>
      }
      {!isMainView && colors.length > 0 &&
        <ColorGridBottom>
          {colors.map((item) => {
            return (
              <ColorSquareBottom
                isSelected={item === currentColor}
                color={item}
                onClick={() => changeColor(item)}
              />
            );
          })}
        </ColorGridBottom>
      }
    </OuterContainer>
  );
}

export default ColorPicker;

const OuterContainer = styled.div({
  flex: 1,
  height: '100%',
  width: '100%',
});

const ColorGrid = styled.div({
  height: '100%',
  display: 'flex',
  flexWrap: 'wrap',
  flexDirection: 'row',
  justifyContent: 'flex-start',
  alignContent: 'flex-start',
  backgroundColor: white,
  marginTop: 10,
  [media(Breakpoint.lg)]: {
    marginTop: LayoutUtils.STANDARD_MAIN_UI_SQUARE_MARGIN,
  },
});

const ColorSquare = styled.button<ColorContainerProps>(({ isSelected, color }) => ({
  height: LayoutUtils.STANDARD_MAIN_UI_SQUARE_SIDE,
  width: LayoutUtils.STANDARD_MAIN_UI_SQUARE_SIDE,
  borderWidth: LayoutUtils.STANDARD_BORDER_WIDTH,
  borderColor: isSelected ? black : lighterGray,
  borderStyle: 'solid',
  borderRadius: 4,
  backgroundColor: color,
  marginLeft: 8,
  marginBottom: 8,
  [media(Breakpoint.lg)]: {
    marginLeft: LayoutUtils.STANDARD_MAIN_UI_SQUARE_MARGIN,
    marginBottom: LayoutUtils.STANDARD_MAIN_UI_SQUARE_MARGIN,
  },
}));

const ColorGridBottom = styled.div({
  height: 56,
  display: 'flex',
  flexWrap: 'nowrap',
  flexDirection: 'row',
  justifyContent: 'space-evenly',
  alignItems: 'center',
  backgroundColor: white,
  [media(Breakpoint.lg)]: {
    height: LayoutUtils.STANDARD_SUPPORT_UI_WINDOW_HEIGHT,
  },
});

const ColorSquareBottom = styled.button<ColorContainerProps>(({ isSelected, color }) => ({
  width: 28,
  height: 28,
  borderWidth: isSelected ? 4 : 0,
  borderColor: purpleBlack,
  borderRadius: 4,
  backgroundColor: color,
  [media(Breakpoint.lg)]: {
    width: LayoutUtils.STANDARD_SUPPORT_UI_SQUARE_SIDE,
    height: LayoutUtils.STANDARD_SUPPORT_UI_SQUARE_SIDE,
  },
}));
